import { adaptFileResponse } from "@/app/adapter/fileAdapter";
import DownloadHelper from "@/app/helpers/DownloadHelper";
import {
  Attachments,
  EagleViewDocument,
} from "@/app/interfaces/response/OrderDetailsResponse";
import dataService from "@/app/services/dataService";
import { NotificationTypeEnum } from "@/enums/notificationTypeEnum";
import {
  Paper,
  Typography,
  Box,
  Link,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useGlobalNotification } from "../../header/GlobalNotificationContext";
import { DOWNLOAD_FILE_ERROR } from "@/app/constants/constants";
import PageLoadingOverlay from "../../shared/pageLoadingOverlay";
import AccordionSection from "../../shared/accordionSection";

const LOADING_MESSAGE = "We are preparing your file. Please wait.";
const TITLE_ATTACHED_DOCUMENTS = "Attached documents";

const AttachedDocuments = ({
  attachments,
  eagleViewDocuments = [],
  orderId,
}: {
  attachments: Attachments[];
  eagleViewDocuments?: EagleViewDocument[];
  orderId: number;
}) => {
  const { setNotification } = useGlobalNotification();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const downloadAttachment = useMutation({
    mutationFn: (attachmentId: number) =>
      dataService.downloadAttachmentRaw(attachmentId),
    onSuccess: (res) => {
      const { blob, filename } = adaptFileResponse(res);
      DownloadHelper.triggerDownload(blob, filename);
    },
    onError: async () => {
      setNotification(DOWNLOAD_FILE_ERROR, NotificationTypeEnum.ERROR);
    },
  });

  const downloadEagleViewDocument = useMutation({
    mutationFn: () => dataService.downloadEagleViewDocumentRaw(orderId),
    onSuccess: (res) => {
      const { blob, filename } = adaptFileResponse(res);
      DownloadHelper.triggerDownload(blob, filename);
    },
    onError: async () => {
      setNotification(DOWNLOAD_FILE_ERROR, NotificationTypeEnum.ERROR);
    },
  });

  const content = (
    <Paper
      elevation={0}
      sx={{
        p: isMobile ? 1.5 : 2,
        mt: 1.5,
        borderRadius: 2,
        boxShadow: {
          xs: "0 0px 0px rgba(0,0,0,0)",
          md: "0 1px 3px rgba(0,0,0,0.1)",
        },
        bgcolor: "white",
      }}
    >
      {!isMobile && (
        <Typography variant="subtitle1" fontWeight={600} mb={1}>
          {TITLE_ATTACHED_DOCUMENTS}
        </Typography>
      )}

      {attachments.length === 0 && eagleViewDocuments.length === 0 ? (
        <Box sx={{ color: "text.secondary" }}>
          There are no attached documents.
        </Box>
      ) : (
        <>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {attachments.map((attachment) => (
              <Link
                key={attachment.id}
                component="button"
                variant="body2"
                sx={{
                  textAlign: "left",
                  color: "primary.main",
                  cursor: "pointer",
                  fontWeight: "bold",
                  textDecoration: "none",
                  "&:hover": { textDecoration: "none" },
                }}
                onClick={() => downloadAttachment.mutate(attachment.id)}
              >
                {attachment.fileName}
              </Link>
            ))}
            {eagleViewDocuments.map((doc) => (
              <Link
                key={doc.fileName}
                component="button"
                variant="body2"
                sx={{
                  textAlign: "left",
                  color: "primary.main",
                  cursor: "pointer",
                  fontWeight: "bold",
                  textDecoration: "none",
                  "&:hover": { textDecoration: "none" },
                }}
                onClick={() => downloadEagleViewDocument.mutate()}
              >
                {doc.fileName}
              </Link>
            ))}
          </Box>
          {(downloadAttachment.isPending ||
            downloadEagleViewDocument.isPending) && (
            <PageLoadingOverlay text={LOADING_MESSAGE} />
          )}
        </>
      )}
    </Paper>
  );

  return isMobile ? (
    <AccordionSection title={TITLE_ATTACHED_DOCUMENTS}>
      {content}
    </AccordionSection>
  ) : (
    content
  );
};

export default AttachedDocuments;
